// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Document,
  Catalog,
  Add,
  DocumentImport,
  CheckmarkOutline,
  Rule,
  Link as LinkIcon,
  Notification,
  UserMultiple,
  Group,
} from '@carbon/icons-react';
import { useFlagOn } from '../../shared/featureFlags';
import styles from './AdminSidebar.module.css';

interface NavItem {
  label: string;
  to: string;
  icon: React.ComponentType<{ size?: number }>;
}

const LIBRARY_ITEM: NavItem = {
  label: 'Library',
  to: '/admin-center/procedures/library',
  icon: Catalog,
};

const ADD_PROCEDURE_ITEM: NavItem = {
  label: 'Add procedure',
  to: '/admin-center/procedures/add',
  icon: Add,
};

// Temporary, non-destructive override: force the nav item hidden regardless
// of the `addProcedureEnabled` LaunchDarkly flag. The authoring flow it leads
// to has nothing to save to yet — SOP Factory's catalog is read-only and the
// BFF isn't wired to helios-adm's PR-authoring endpoint (#helios-integration,
// Oct 2026). The route, AddProcedurePage, and the flag itself are untouched —
// direct links keep working and nothing here needs reverting beyond this
// flag flip once the authoring flow is wired up.
const ADD_PROCEDURE_FORCE_HIDDEN = true;

// Same temporary, non-destructive override for Bulk import (AB#34512). The
// page is a static visual scaffold with no BFF connection yet, so it must not
// be reachable from the menu in dev until that wiring lands. The route,
// BulkImportPage, and the `bulkImportEnabled` flag are untouched — direct
// links keep working and the only revert needed is flipping this to false.
const BULK_IMPORT_FORCE_HIDDEN = true;

const BULK_IMPORT_ITEM: NavItem = {
  label: 'Bulk import',
  to: '/admin-center/procedures/bulk-import',
  icon: DocumentImport,
};

const REVIEW_QUEUE_ITEM: NavItem = {
  label: 'Review queue',
  to: '/admin-center/procedures/review-queue',
  icon: CheckmarkOutline,
};

const RULE_ITEMS: NavItem[] = [
  { label: 'All rules', to: '/admin-center/rules', icon: LinkIcon },
  { label: 'Alarm definitions', to: '/admin-center/rules/alarm-definitions', icon: Notification },
];

// Temporary, non-destructive: hides the "Rules" section from the nav. The
// routes (`rules`, `rules/alarm-definitions`) stay mounted in
// AdminCenterRoutes.tsx and still render their PlaceholderPage — a direct
// link keeps working — this only removes the sidebar entry. Flip this back
// to `true` to show it again; nothing else needs to change.
const RULES_NAV_VISIBLE = false;

const ACCESS_ITEMS: NavItem[] = [
  { label: 'Users', to: '/admin-center/users', icon: UserMultiple },
  { label: 'Roles', to: '/admin-center/roles', icon: Group },
];

const linkClassName = ({ isActive }: { isActive: boolean }) =>
  isActive ? `${styles.link} ${styles.linkActive}` : styles.link;

const topLinkClassName = ({ isActive }: { isActive: boolean }) =>
  isActive ? `${styles.topLink} ${styles.linkActive}` : styles.topLink;

interface NavGroupProps {
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  items: NavItem[];
}

/**
 * A section (Procedures / Rules / Access) has no open/closed state of its
 * own — whether it shows its children is entirely derived from whether the
 * current route is one of them. Clicking the header always navigates to the
 * first child; that navigation is what makes the section "expand," the same
 * way clicking any of its children would. There's no separate collapsed vs.
 * expanded toggle to get out of sync with the router.
 */
function NavGroup({ label, icon: Icon, items }: NavGroupProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isSectionActive = items.some(item => location.pathname === item.to);

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={
          isSectionActive
            ? `${styles.sectionToggle} ${styles.sectionToggleActive}`
            : styles.sectionToggle
        }
        onClick={() => navigate(items[0].to)}
        aria-current={isSectionActive ? 'true' : undefined}
      >
        <Icon size={18} />
        {label}
      </button>
      {isSectionActive && (
        <div className={styles.subItems}>
          {items.map(item => (
            <NavLink key={item.to} to={item.to} className={linkClassName}>
              <item.icon size={14} />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Left nav. Home, then three sections — Procedures, Rules (bindings between
 * alarm definitions and procedures), and Access (Users / Roles). Landing on
 * `/admin-center` matches none of the sections, so Home is the only thing
 * selected by default. Every entry routes somewhere — unbuilt sections point
 * at PlaceholderPage rather than dead-ending (see admin-center/README.md).
 */
export default function AdminSidebar() {
  const addProcedureEnabled = useFlagOn('addProcedureEnabled') && !ADD_PROCEDURE_FORCE_HIDDEN;
  const bulkImportEnabled = useFlagOn('bulkImportEnabled') && !BULK_IMPORT_FORCE_HIDDEN;
  const reviewQueueEnabled = useFlagOn('reviewQueueEnabled');
  // Same positions Add procedure, Bulk import and Review queue have always had
  // — each flag decides whether its item is there, not where.
  const procedureItems = [
    LIBRARY_ITEM,
    ...(addProcedureEnabled ? [ADD_PROCEDURE_ITEM] : []),
    ...(bulkImportEnabled ? [BULK_IMPORT_ITEM] : []),
    ...(reviewQueueEnabled ? [REVIEW_QUEUE_ITEM] : []),
  ];

  // Access (Users + Roles) is gated as one unit — nav half of the dual gate;
  // AdminCenterRoutes.tsx redirects the two routes for the route half.
  const accessManagementEnabled = useFlagOn('accessManagementEnabled');

  return (
    <nav className={styles.sidebar} aria-label="Admin Center">
      <div className={styles.navList}>
        <NavLink to="/admin-center" className={topLinkClassName} end>
          <Home size={18} />
          Home
        </NavLink>

        <NavGroup label="Procedures" icon={Document} items={procedureItems} />
        {RULES_NAV_VISIBLE && <NavGroup label="Rules" icon={Rule} items={RULE_ITEMS} />}
        {accessManagementEnabled && (
          <NavGroup label="Access" icon={UserMultiple} items={ACCESS_ITEMS} />
        )}
      </div>

      <div className={styles.footer}>Admin Center 0.1.1</div>
    </nav>
  );
}
